import React from 'react';

/**
 * Faixa informativa do design system (at-65, frente D).
 *
 * É o bloco de texto que **responde uma pergunta antes de ela ser feita** —
 * *"preciso fazer alguma coisa com isso?"*, *"por que este número não muda com a
 * busca?"*, *"por que este cadastro não aparece aqui?"*. Aparece uma vez no topo
 * da tela (ou de uma seção), em vez de repetir a mesma ressalva dentro de cada
 * linha.
 *
 * 🔴 **Nasce porque o padrão já existia copiado quatro vezes**, cada uma com a
 * própria geometria e a própria cor: `.carteira-legenda` (faixa laranja, borda
 * esquerda de 3px), `.vistoria-painel-legenda` (o CSS dela dizia, em comentário,
 * *"mesmo desenho da `.carteira-legenda`"*), a nota do `ModalDetalhesAdesao`
 * (`.adesao-detalhes-nota`) e a `.rascunho-aviso` de `/associado`. Esta branch
 * tem **dois** consumidores próprios: a legenda do topo da carteira e a nota do
 * envio automático no modal de detalhes.
 *
 * 📌 **A at-63 fez uma quinta cópia**, e ela NÃO foi migrada aqui de propósito: o
 * painel `/vistoria` reescreveu a legenda como um `<p>` com estes mesmos
 * utilitários inline. Trocá-la por este componente é a coisa certa a fazer, mas
 * mexeria numa tela recém-validada por uma diferença de `line-height` — fica para
 * quem tocar naquele arquivo. É de lá que a aparência abaixo foi copiada.
 *
 * ⚠️ **A aparência padrão é a NEUTRA da at-63**, não o laranja de borda-esquerda
 * que a `.carteira-legenda` usava. Não é gosto: laranja é a cor da marca e o
 * projeto reserva significado às cores (spec 01 §2.1) — uma faixa laranja
 * permanente no topo de uma tela de listagem compete com a ação principal e,
 * pior, treina a pessoa a ignorar o laranja.
 *
 * ⚠️ **Não é `ErrorState` nem `EmptyState`.** Aqui o conteúdo carregou e está
 * certo; a nota só contextualiza. Falha de carga é `ErrorState`, sempre.
 *
 * ⚠️ **Sem ícone e sem emoji** (regra de Victor, 06/09/2026): o marcador é o
 * próprio texto. Quando a nota precisar chamar atenção de verdade, o caminho é
 * escrever a primeira frase em negrito — como o painel de vistoria faz com
 * *"O envio ao SGA é **automático**"*.
 */

export type VarianteNota = 'neutra' | 'atencao';

/**
 * 🔴 **`atencao` nasceu na at-66 com um caso concreto, não por simetria:** a tela
 * de Acompanhamento de Oficina precisa dizer *"não foi possível falar com a
 * oficina agora — os dados abaixo são da última consulta que deu certo"*. Isso
 * **não é erro da tela** (o conteúdo está lá e é legítimo, só pode estar velho),
 * mas também não é informação neutra: quem lê precisa saber antes de repassar o
 * dado ao associado. A tela antiga pintava esse caso com o **mesmo vermelho** da
 * falha de carga, e aí as duas situações — "a tela não carregou" e "a tela
 * carregou com dado de ontem" — ficavam indistinguíveis.
 *
 * ⚠️ **Vermelho continua sendo só do `ErrorState`.** A divisão é: `ErrorState`
 * = não há conteúdo; `Nota atencao` = há conteúdo, com ressalva; `Nota` neutra =
 * há conteúdo e a nota só contextualiza. Pendência de registro segue sendo
 * `Chip pendente` (spec 02 §2.3).
 */
const POR_VARIANTE: Record<VarianteNota, string> = {
  neutra: 'tw-border-superficie-borda tw-bg-superficie tw-text-texto-suave',
  /* Mesmos tokens do `Chip pendente` — o grupo "Atenção" do `13-ui-kit.md`. */
  atencao: 'tw-border-amber-warn/40 tw-bg-amber-bg tw-text-amber-deep',
};

interface PropsNota extends React.HTMLAttributes<HTMLDivElement> {
  /** Ver `POR_VARIANTE`. Padrão `neutra` — a aparência de sempre. */
  variante?: VarianteNota;
  /**
   * Texto que se atualiza sozinho (o andamento de uma operação, por exemplo).
   *
   * ⚠️ Só passe quando o conteúdo **mudar enquanto a tela está aberta** — uma
   * nota estática com `aria-live` faria o leitor de tela reanunciá-la a cada
   * re-render vizinho, virando ruído.
   */
  'aria-live'?: 'polite' | 'assertive' | 'off';
  children: React.ReactNode;
}

const Nota: React.FC<PropsNota> = ({
  variante = 'neutra',
  className = '',
  children,
  ...resto
}) => (
  <div
    /*
     * `role="note"` e não `<aside>`: o conteúdo é complementar ao da tela, não
     * uma seção navegável — e o leitor de tela o anuncia como nota em vez de
     * listá-lo no índice de regiões.
     */
    role="note"
    className={[
      'tw-rounded-xl tw-border tw-px-3 tw-py-2.5 tw-text-xs tw-leading-relaxed',
      POR_VARIANTE[variante],
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    {...resto}
  >
    {children}
  </div>
);

export default Nota;
